import { useEffect, useState } from 'react';
import { useGetSystemSettingsQuery } from '@SERVICES';

interface TinyMCELoadState {
    isLoaded: boolean;
    isLoading: boolean;
    error: string | null;
}

declare global {
    interface Window {
        tinymce: any;
    }
}

export function useTinyMCE(): TinyMCELoadState {
    const [loadState, setLoadState] = useState<TinyMCELoadState>({
        isLoaded: false,
        isLoading: false,
        error: null,
    });

    const { data: systemSettings, isLoading: isSettingsLoading, error: settingsError } = useGetSystemSettingsQuery();

    useEffect(() => {
        // Don't proceed if settings are still loading or if there's an error
        if (isSettingsLoading || settingsError) {
            return;
        }

        // Check if TinyMCE is already loaded
        if (window.tinymce) {
            setLoadState({
                isLoaded: true,
                isLoading: false,
                error: null,
            });
            return;
        }

        // Check if we have system settings with API token
        if (!systemSettings?.wysiwygApiToken) {
            setLoadState({
                isLoaded: false,
                isLoading: false,
                error: 'WYSIWYG API token not configured in system settings',
            });
            return;
        }

        setLoadState((prev) => ({ ...prev, isLoading: true }));

        // Load TinyMCE scripts dynamically
        const loadTinyMCE = async () => {
            try {
                // Load core TinyMCE first
                const coreScript = document.createElement('script');
                coreScript.src = '/tinymce/tinymce.min.js';
                coreScript.defer = true;
                document.head.appendChild(coreScript);

                await new Promise((resolve, reject) => {
                    coreScript.onload = resolve;
                    coreScript.onerror = reject;
                });

                // Load plugins with API key
                const pluginsScript = document.createElement('script');
                const apiUrl = systemSettings.wysiwygApiUrl || 'https://cdn.tiny.cloud/1';
                pluginsScript.src = `${apiUrl}/${systemSettings.wysiwygApiToken}/tinymce/6/plugins.min.js`;
                pluginsScript.defer = true;
                document.head.appendChild(pluginsScript);

                await new Promise((resolve, reject) => {
                    pluginsScript.onload = resolve;
                    pluginsScript.onerror = reject;
                });

                setLoadState({
                    isLoaded: true,
                    isLoading: false,
                    error: null,
                });
            } catch (error) {
                setLoadState({
                    isLoaded: false,
                    isLoading: false,
                    error: `Failed to load TinyMCE: ${error}`,
                });
            }
        };

        loadTinyMCE();
    }, [systemSettings, isSettingsLoading, settingsError]);

    return loadState;
} 